import { auth } from '@/lib/firebase.ts';
import {
  BackendApiError,
  KnownBackendApiError,
  MissingTenantError,
  UnauthenticatedError,
} from '@/lib/api/exception.ts';
import { queryClient } from '@/App.tsx';
import { MyAccessDto } from '@/features/dashboard/_root/types/MyAccessDto.ts';
import { resolveCurrentCompanyId } from '@/features/dashboard/_root/access/currentCompany.ts';
import { userApiQueryKeys } from '@/features/dashboard/_root/api/userApi.queryKeys.ts';
import { BackendApiErrorDto } from '@/lib/api/apiErrorDto.ts';
import { getMeQueryOptions } from '@/features/dashboard/_root/hooks/useMe.ts';
import { buildRequestHeaders } from '@/lib/api/requestHeaders.ts';

export interface FetchApiOptions {
  keepalive?: boolean;
  headers?: Record<string, string>;
}

export const fetchPublicApi = async <RespType>(
  method: 'GET' | 'POST' | 'PUT',
  path: string,
  body?: unknown,
  options?: FetchApiOptions,
): Promise<RespType> => {
  return fetchApi<RespType>(method, path, body, undefined, undefined, options);
};

interface FetchPrivateApiOptions {
  skipTenantId?: boolean;
  headers?: Record<string, string>;
}

export const fetchPrivateApi = async <RespType>(
  method: 'GET' | 'POST' | 'DELETE' | 'PUT' | 'PATCH',
  path: string,
  body?: unknown,
  options?: FetchPrivateApiOptions,
): Promise<RespType> => {
  await auth.authStateReady();
  const token = await auth?.currentUser?.getIdToken();
  if (!token) {
    throw new UnauthenticatedError();
  }

  let tenantId: string | null = null;
  if (!options?.skipTenantId) {
    tenantId = await getTenantId();
    if (!tenantId) {
      throw new MissingTenantError();
    }
  }

  return fetchApi(method, path, body, token, tenantId, {
    headers: options?.headers,
  });
};

async function getTenantId(): Promise<string | null> {
  // Fast path: read from cache (zero latency in normal case)
  const cached = queryClient.getQueryData<MyAccessDto>(userApiQueryKeys.me());
  const fromCache = resolveCurrentCompanyId(cached);
  if (fromCache) return fromCache;

  // Slow path: cache is empty after idle, re-fetch
  const userAccess = await queryClient.ensureQueryData(
    getMeQueryOptions(auth.currentUser),
  );
  return resolveCurrentCompanyId(userAccess);
}

const fetchApi = async <RespType>(
  method: 'GET' | 'POST' | 'DELETE' | 'PUT' | 'PATCH',
  path: string,
  body?: unknown,
  token?: string,
  tenantId?: string | null,
  options?: FetchApiOptions,
): Promise<RespType> => {
  const serverApiUrl = import.meta.env.VITE_CAMPDESK_API_URL;
  const combinedPath = `${serverApiUrl}${path}`;

  const requestInit: RequestInit = {
    headers: buildRequestHeaders(token, tenantId, options?.headers),
    method: method,
    cache: 'no-store',
  };
  // Zdarzenie lejka ma dolecieć także wtedy, gdy klient zamyka kartę w trakcie
  // żądania; `keepalive` zdejmuje je z cyklu życia dokumentu.
  if (options?.keepalive) {
    requestInit.keepalive = true;
  }
  if (body) {
    requestInit.body = JSON.stringify(body);
  }
  const res = await fetch(combinedPath, requestInit);
  if (!res.ok) {
    try {
      const errorJson: BackendApiErrorDto = await res.json();
      throw new KnownBackendApiError(errorJson, res.status);
    } catch (error) {
      // Throw KnownBackendApiError if parsing errorJson succeeded and there were errors
      if (error instanceof KnownBackendApiError) {
        throw error;
      }
      throw new BackendApiError(res.status);
    }
  }

  if (res.headers.get('Content-Type') === 'application/json') {
    return <RespType>await res.json();
  }
  return <RespType>null;
};
